import { useMemo } from 'react'
import { IntlShape, useIntl } from 'react-intl'

import { format } from 'date-fns'

import { PRODUCTION_NEURODIVERSITY_CARE_ADVOCACY_BOOKING_LINK } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import {
  DirectLinkIntent,
  ousPrograms,
  ProgramCustomerPropertyId,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/customerProgram'
import { PRODUCT_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/productType'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Programs } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import { buildDirectBookingLink } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { DirectLinkSource } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'
import { getModifiedProgramCoverageForBenefitsEligibility } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils'
import { CustomerInfo } from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'
import { LTProgramName, ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { ProgramCoverageBreakdown, YearProgramCoverage } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'

import {
  coverageStates,
  LWUrl,
  programConfig as programConfigConstant,
} from '../../../../../common/constants/appConstants'

/**
 * Programs to display as capsules if the client is eligible for the program.
 * The ordering here will be the same as the ordering in the UI.
 * See `buildProgramLinks`
 */
const ELIGIBILITY_CAPSULE_PROGRAM_NAMES: ProgramNames[] = [
  ProgramNames.BlendedCareTherapy,
  ProgramNames.DialecticalBehaviorTherapy,
  ProgramNames.AlcoholUseDisorderTherapy,
  ProgramNames.Coaching,
  ProgramNames.CoachingForParents,
  ProgramNames.SingleSessionCoaching,
  ProgramNames.MedicationManagement,
  ProgramNames.ClinicalLeaveEvaluation,
  ProgramNames.SubstanceUseEvaluation,
  ProgramNames.DIRECT_ACCESS_THERAPY,
  ProgramNames.DIRECT_ACCESS_MEDS,
  ProgramNames.CaregiverTrainingForNeuro,
  ProgramNames.NeurodiversitySkillsCoaching,
]

const NICOTINE_QUITLINE_BOOKING_LINK = 'https://go.oncehub.com/NicotineQuitline?referralsource=NqProviderReferral'
const NICOTINE_QUITLINE_LABEL = 'Nicotine Quitline'
const CAREGIVER_TRAINING_TOOLTIP =
  'Your client will be routed to a Neurodiversity Care Advocate to screen the member before they are booked into Caregiver Training.'

type ProgramLinkEntry = {
  customerPropertyId: ProgramCustomerPropertyId
  isLyraProgram: boolean
  label: string
  bookingLink?: string
}

function buildProgramLinks(intl: IntlShape, employer?: string): ProgramLinkEntry[] {
  return ELIGIBILITY_CAPSULE_PROGRAM_NAMES.map((programName) => {
    const config = Programs.getProgramConfigByName(programName)

    // Preserves existing behavior for `ProgramCustomerPropertyId.directAccessTherapy` -
    // Uses ICAS as the partner in the booking link
    const bookingLinkProgram =
      programName === ProgramNames.DIRECT_ACCESS_THERAPY ? ProgramNames.ICASTherapy : programName

    return {
      customerPropertyId: config.customerPropertyId,
      isLyraProgram: config.blendedCareProgramId !== null,
      label: intl.formatMessage(config.displayLabels.default),
      bookingLink: employer
        ? programName === ProgramNames.CaregiverTrainingForNeuro
          ? PRODUCTION_NEURODIVERSITY_CARE_ADVOCACY_BOOKING_LINK
          : getProviderReferralBookingLink(bookingLinkProgram, employer)
        : undefined,
    }
  }).concat([
    {
      customerPropertyId: ProgramCustomerPropertyId.nicotineQuitline,
      isLyraProgram: false,
      label: NICOTINE_QUITLINE_LABEL,
      bookingLink: NICOTINE_QUITLINE_BOOKING_LINK,
    },
  ])
}

function getProviderReferralBookingLink(programName: ProgramNames, employer: string): string | undefined {
  return buildDirectBookingLink({
    programName,
    directLinkIntent: DirectLinkIntent.DIRECT_LINK_CONCURRENT_CARE_REFERRAL,
    directLinkSource: DirectLinkSource.PROVIDER_MESSAGE,
    fallbackUrl: `${LWUrl(employer)}/secure/onboard/match-location`,
  })
}

function shouldShowProgramBookingLink({
  programName,
  bookingLinkProgram,
  clientCountryCode,
}: {
  programName: string
  bookingLinkProgram: string
  clientCountryCode?: string
}): boolean {
  const result =
    programConfigConstant[programName as LTProgramName]?.programBookingLinksAllowed.includes(bookingLinkProgram) ||
    (clientCountryCode !== 'US' && bookingLinkProgram === ProgramCustomerPropertyId.directAccessTherapy)

  return result
}

function getIsProgramCovered({
  programCustomerPropertyId,
  modifiedProgramCoverageBreakdown,
  clientCustomer,
}: {
  programCustomerPropertyId: ProgramCustomerPropertyId
  modifiedProgramCoverageBreakdown: YearProgramCoverage
  clientCustomer?: { lyra_nicotine_quitline_enabled?: string | boolean }
}): boolean {
  const nonProgramOfferings = [
    (clientCustomer?.lyra_nicotine_quitline_enabled === true ||
      clientCustomer?.lyra_nicotine_quitline_enabled === 'true') &&
      ProgramCustomerPropertyId.nicotineQuitline,
  ].filter(Boolean)

  const coverageKey = programCustomerPropertyId as keyof YearProgramCoverage
  const isInCoverageBreakdown = modifiedProgramCoverageBreakdown[coverageKey] != null
  const isInNonProgramOfferings = nonProgramOfferings?.includes(programCustomerPropertyId)

  if (!isInCoverageBreakdown && isInNonProgramOfferings) {
    return true
  }

  const coverage = modifiedProgramCoverageBreakdown[coverageKey]
  return (
    isInCoverageBreakdown && !!(coverage?.eapSupported || coverage?.bhbSupported || coverage?.specialtyBhbSupported)
  )
}

function getProgramCoverageDescription(
  programCustomerPropertyId: string,
  modifiedProgramCoverageBreakdown: YearProgramCoverage,
  employer?: string,
  isLyraSelectClient?: boolean,
): string | undefined {
  if (programCustomerPropertyId === ProgramCustomerPropertyId.nicotineQuitline) {
    const isRenewSupported =
      modifiedProgramCoverageBreakdown[ProgramCustomerPropertyId.alcoholUseDisorderTherapy]?.eapSupported ||
      modifiedProgramCoverageBreakdown[ProgramCustomerPropertyId.alcoholUseDisorderTherapy]?.bhbSupported ||
      modifiedProgramCoverageBreakdown[ProgramCustomerPropertyId.alcoholUseDisorderTherapy]?.specialtyBhbSupported
    return isRenewSupported ? coverageStates.NICOTINE : coverageStates.NICOTINE_RENEW_DISABLED
  } else if (programCustomerPropertyId === ProgramCustomerPropertyId.clinicalLeaveEvaluation && employer === 'apple') {
    // LCDELIVERY-6226: For Apple clients only, show a custom message for CLE
    return 'Please consult with your manager to see if your client might be eligible.'
  } else if (programCustomerPropertyId in modifiedProgramCoverageBreakdown) {
    const data = modifiedProgramCoverageBreakdown[programCustomerPropertyId as keyof YearProgramCoverage]
    if (!data) return undefined
    const { eapSupported, bhbSupported, specialtyBhbSupported } = data

    let coverageDescription = undefined

    if (!eapSupported && !bhbSupported && specialtyBhbSupported) {
      coverageDescription = coverageStates.SPECIALTY
    } else if (!eapSupported && bhbSupported && !specialtyBhbSupported) {
      coverageDescription = coverageStates.HP
    } else if (!eapSupported && bhbSupported && specialtyBhbSupported) {
      coverageDescription = coverageStates.HP_SPECIALTY
    } else if (eapSupported && !bhbSupported && !specialtyBhbSupported) {
      coverageDescription = isLyraSelectClient ? coverageStates.EAP_SELECT : coverageStates.EAP
    } else if (eapSupported && !bhbSupported && specialtyBhbSupported) {
      coverageDescription = coverageStates.EAP_SPECIALTY
    } else if (eapSupported && bhbSupported && !specialtyBhbSupported) {
      coverageDescription = coverageStates.EAP_HP
    } else if (eapSupported && bhbSupported && specialtyBhbSupported) {
      coverageDescription = coverageStates.EAP_HP_SPECIALTY
    }

    // Append dependent restriction message for clinical leave evaluation
    if (programCustomerPropertyId === ProgramCustomerPropertyId.clinicalLeaveEvaluation && coverageDescription) {
      coverageDescription += ' This offering is not available for dependents.'
    }

    return coverageDescription
  }
  return undefined
}

export type EligibilityCapsuleDetails = {
  testID: string
  program: string
  label: string
  coverageDescription: string | undefined
  tooltip?: string
  hasLink: boolean
  link: string | undefined
  isLyraProgram: boolean
}

export function useEligibilityCapsuleDetails(
  programName: string,
  programCoverageBreakdown?: ProgramCoverageBreakdown,
  clientDetails?: { employer?: string; country?: string; product_type: string; date_of_birth?: string },
  clientCustomer?: CustomerInfo,
): EligibilityCapsuleDetails[] {
  const intl = useIntl()
  const { forceAdultFlowsToHPI } = useFlags()

  const employer = clientDetails?.employer
  const programLinks = useMemo(() => buildProgramLinks(intl, employer), [intl, employer])
  const currentProgramCoverageBreakdown = programCoverageBreakdown?.[format(new Date(), 'yyyy')] as
    | ProgramCoverageBreakdown
    | undefined
  const isLyraSelectClient = clientDetails?.product_type === PRODUCT_TYPE.SELECT
  const clientCountryCode = clientDetails?.country
  const currentProgramConfig = programConfigConstant[programName as LTProgramName]

  // Apply the same logic as BenefitsEligibility for modifying program coverage
  const modifiedProgramCoverageBreakdown = getModifiedProgramCoverageForBenefitsEligibility(
    forceAdultFlowsToHPI,
    currentProgramCoverageBreakdown || {},
    clientDetails
      ? { employer: clientDetails.employer || '', date_of_birth: clientDetails.date_of_birth || '' }
      : undefined,
  )

  return programLinks
    .filter(({ customerPropertyId }) => {
      return (
        getIsProgramCovered({
          programCustomerPropertyId: customerPropertyId,
          modifiedProgramCoverageBreakdown,
          clientCustomer,
        }) &&
        (clientCountryCode === 'US' || ousPrograms.includes(customerPropertyId)) &&
        (!currentProgramConfig?.programsToDisplay ||
          currentProgramConfig.programsToDisplay.includes(customerPropertyId))
      )
    })
    .map(({ customerPropertyId, isLyraProgram, label, bookingLink }): EligibilityCapsuleDetails | null => {
      const showBookingLink = shouldShowProgramBookingLink({
        programName,
        bookingLinkProgram: customerPropertyId,
        clientCountryCode,
      })

      const hideNonBookingLinkPrograms = isLyraProgram && programName === ProgramNames.TeensTherapy
      if (hideNonBookingLinkPrograms && (bookingLink == null || !showBookingLink)) {
        return null
      }

      const link = (showBookingLink && bookingLink) || undefined

      return {
        testID: `BenefitsEligibility-${customerPropertyId}`,
        program: customerPropertyId,
        label: currentProgramConfig?.programTitleOverride?.[customerPropertyId] || label,
        coverageDescription: getProgramCoverageDescription(
          customerPropertyId,
          modifiedProgramCoverageBreakdown,
          employer,
          isLyraSelectClient,
        ),
        tooltip:
          customerPropertyId === ProgramCustomerPropertyId.caregiverTrainingForNeuro
            ? CAREGIVER_TRAINING_TOOLTIP
            : undefined,
        hasLink: !!link,
        link,
        isLyraProgram,
      }
    })
    .filter(Boolean as unknown as (value: EligibilityCapsuleDetails | null) => value is EligibilityCapsuleDetails)
}
